import { formatInTimeZone } from 'date-fns-tz';

/** APAC default IANA timezone — matches backend `Facility.time_zone NOT NULL DEFAULT 'Asia/Singapore'` (R-6.6 MR A). */
export const APAC_DEFAULT_TZ = 'Asia/Singapore';

const DEFAULT_PATTERN = 'yyyy-MM-dd HH:mm zzz';

/**
 * Render an instant in a deliberate IANA timezone.
 *
 * Requires date-fns-tz v3 API (function name `formatInTimeZone`); v2's `format` was renamed.
 * Do not silently downgrade — v2 has a different surface that will fail to type-check here.
 *
 * @param instant ISO-8601 string or Date. Falsy or unparseable → empty string.
 * @param tz     IANA zone (e.g. 'Asia/Singapore'). Empty/invalid → UTC fallback.
 * @param pattern date-fns format pattern. Defaults to 'yyyy-MM-dd HH:mm zzz'.
 */
export function formatTime(
  instant: string | Date,
  tz: string,
  pattern: string = DEFAULT_PATTERN,
): string {
  if (!instant) return '';
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatInTimeZone(d, tz || 'UTC', pattern);
  } catch {
    return formatInTimeZone(d, 'UTC', pattern);
  }
}

/**
 * Canonical key for an instant-valued column/bucket: the UTC `Z` spelling of
 * the same millisecond. The same instant in two spellings (`Z` vs `+00:00`,
 * millis vs none) must not render as two identically-headed columns.
 * Unparseable input passes through unchanged so the caller still renders
 * something rather than dropping the column.
 */
export function canonicalInstantKey(value: string): string {
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return value;
  return new Date(ms).toISOString();
}

/**
 * The wall-clock calendar day `instant` falls on in `tz`, as a browser-local
 * midnight `Date` — for day-key math (MAR days, week grids) that must run on
 * tz days rather than browser days. A browser-local `startOfDay(new Date())`
 * is the wrong day for any viewer east/west of their profile tz near
 * midnight; anchoring the day in tz keeps bucket keys, query ranges and
 * labels on the same day. Unparseable input falls back to today-in-tz;
 * empty/invalid tz falls back to UTC (same contract as `formatTime`).
 */
export function tzDayStart(instant: string | Date, tz: string): Date {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const base = Number.isNaN(d.getTime()) ? new Date() : d;
  let day: string;
  try {
    day = formatInTimeZone(base, tz || 'UTC', 'yyyy-MM-dd');
  } catch {
    day = formatInTimeZone(base, 'UTC', 'yyyy-MM-dd');
  }
  const [y, m, dd] = day.split('-').map(Number);
  return new Date(y, m - 1, dd);
}

/** Epoch millis, or -Infinity when unparseable (sorts last in a newest-first list). */
function instantMs(value: string): number {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

/**
 * Newest-first comparator for instant strings. Lexicographic order equals
 * chronological order only for uniform-format UTC strings; mixed
 * offsets/precisions misorder, so compare by millisecond.
 */
export function compareInstantsDesc(a: string, b: string): number {
  return instantMs(b) - instantMs(a);
}
